<template>
  <main class="card text-center border-0 shadow">
    <section
      v-if="template.headerLogoURIs"
      class="custom-card-header card-header card-img-top"
    >
      <Logos :logos="template.headerLogoURIs" />
    </section>
    <section class="custom-card-body card-body rounded">
      <h3
        class="textFormat card-title"
        :style="{ color: setTitleColor }"
        v-html="formatText(template.title)"
      ></h3>
      <CountdownTimer v-if="isJavascriptCountDown()" :time="extractTime()" />
      <Image v-else-if="template.imageURI" :uri="template.imageURI" />
      <h5
        class="textFormat card-text"
        :style="{ color: setOverviewColor }"
        v-html="formatText(template.overview)"
      ></h5>
      <DisplayFields
        class="card-text"
        v-if="template.displayFields"
        :displayFields="template.displayFields"
        :theme="templateTheme"
      />
      <Inputs
        v-if="template.inputFields"
        @inputUpdated="inputsUpdated"
        :inputs="template.inputFields"
        :answers="inputData"
        :is-Disabled="disabled"
      />
      <p
        class="textFormat card-text"
        :style="{ color: setConclusionColor }"
        v-html="formatText(template.conclusion)"
      ></p>
      <ButtonsComponent
        v-if="template.buttons && !isSilentAuth"
        @buttonClicked="buttonClicked"
        :buttons="template.buttons"
        :theme="templateTheme"
      />
    </section>
    <section
      v-if="template.footerLogoURIs"
      class="custom-card-footer card-img-bottom"
    >
      <FooterLogos :logos="template.footerLogoURIs" />
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, provide, watch } from "vue";
import Logos from "./Logos.vue";
import CountdownTimer from "./CountdownTimer.vue";
import Image from "./Image.vue";
import DisplayFields from "./DisplayFields.vue";
import Inputs from "./Inputs.vue";
import ButtonsComponent from "./ButtonsComponent.vue";
import FooterLogos from "./FooterLogos.vue";

import {
  button,
  theme,
  uiContentJSON,
} from "@entersekt/types/build/3-5-0/endpoint";
import { InputAnswer } from "@/models/template";

const props = defineProps<{
  template: uiContentJSON;
  templateTheme: theme | undefined;
  isSilentAuth: boolean;
}>();

const headerBackgroundColor = ref(props.templateTheme?.headerBackgroundColor);
const bodyBackgroundColor = ref(props.templateTheme?.bodyBackgroundColor);
const footerBackgroundColor = ref(props.templateTheme?.footerBackgroundColor);

const emit = defineEmits(["buttonClicked"]);

const inputData = ref(new Map<string, number | string>());
const disabled = ref(false);

watch(
  () => props.template,
  () => {
    disabled.value = false;
  },
  { deep: true },
);

const black = "#2c3e50";
const boldRegex = /\*\*(.*?)\*\*/gs;
const italicRegex = /\*(.*?)\*/gs;
const timerRegex = /\(((\d+))\)/g;
const externalLinkRegex = /\[(.*?)\]\((.*?)\)/g;

provide("formatTextFunction", formatText);
const setTitleColor = computed((): string => {
  if (props.templateTheme?.titleTextColor) {
    return props.templateTheme?.titleTextColor;
  }
  return black;
});

const setOverviewColor = computed((): string => {
  return props.templateTheme?.overviewTextColor
    ? props.templateTheme.overviewTextColor
    : black;
});
const setConclusionColor = computed((): string => {
  return props.templateTheme?.conclusionTextColor
    ? props.templateTheme?.conclusionTextColor
    : black;
});

function inputsUpdated(inputs: Map<string, string | number>) {
  inputData.value = inputs;
}

function isJavascriptCountDown(): boolean {
  return props.template.imageURI?.includes("javascript:countdownTimer")
    ? true
    : false;
}

function extractTime(): number | undefined {
  const matchRegex = props.template.imageURI?.match(timerRegex);
  if (matchRegex && matchRegex?.length > 0) {
    return +matchRegex[0].slice(1, matchRegex[0].length - 1);
  }
}

// uses regex to find and replace text wrapped with '**' to first make text bold,
// then finds text wrapped with '*' to italicizes the text.
function emphasisText(text: string | undefined): string | undefined {
  if (!text) {
    return text;
  }
  const boldText = text.replace(boldRegex, "<strong>$1</strong>");
  return boldText.replace(italicRegex, "<em>$1</em>");
}

// Find matching string [text](URL) and converts it to an HTML clickable link
function replaceLink(text: string | undefined): string | undefined {
  if (!text) {
    return text;
  }
  return text.replace(externalLinkRegex, `<a href="$2" target="_blank">$1</a>`);
}

function formatText(text: string | undefined): string | undefined {
  let replacedText = replaceLink(text);
  return emphasisText(replacedText);
}

function buttonClicked(
  isDisabled: boolean,
  action: button.action,
  actionNext?: button.actionNext,
) {
  disabled.value = isDisabled;
  const form = document.getElementById("inputs-form") as HTMLFormElement;
  if (form && !form.checkValidity() && action === "CONTINUE") {
    form.classList.add("was-validated");
  } else {
    const inputsArray: InputAnswer[] = [];
    inputData.value.forEach((value, key) => {
      inputsArray.push({ inputID: key, value });
    });
    emit("buttonClicked", action, inputsArray, actionNext);
  }
}
</script>

<style scoped>
main {
  max-width: 95%;
  margin: 10px;
}

.custom-card-header {
  background-color: v-bind(headerBackgroundColor);
}

.custom-card-body {
  padding: default, 30px;
  background-color: v-bind(bodyBackgroundColor);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.custom-card-body > * {
  padding-bottom: 15px;
}

.custom-card-footer {
  background-color: v-bind(footerBackgroundColor);
}
</style>
